import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiTarget, FiEye, FiHeart, FiShield, FiUsers, FiTrendingUp,
  FiCheckCircle, FiArrowRight, FiAward
} from 'react-icons/fi';
import { FaHandshake } from 'react-icons/fa';

const values = [
  { icon: '🤝', title: 'Integrity', desc: 'We conduct business with the highest standards of honesty and transparency in every transaction.', color: '#0F4C81', bg: '#e8f1fb' },
  { icon: '💎', title: 'Trust', desc: 'Building lasting trust with partners, clients, and stakeholders through consistent delivery and reliability.', color: '#28A745', bg: '#d4edda' },
  { icon: '😊', title: 'Customer Satisfaction', desc: 'Our partners\' success drives everything we do. We go above and beyond to ensure satisfaction.', color: '#F39C12', bg: '#fef5e7' },
  { icon: '✅', title: 'Quality', desc: 'Maintaining uncompromising quality standards across our entire product portfolio and operations.', color: '#9b59b6', bg: '#f5eef8' },
  { icon: '💡', title: 'Innovation', desc: 'Continuously improving our distribution processes, technology, and services to stay ahead.', color: '#0097a7', bg: '#e0f7fa' },
  { icon: '📈', title: 'Growth', desc: 'Committed to driving mutual growth for our partners, employees, and the communities we serve.', color: '#c62828', bg: '#fce4ec' },
];

const milestones = [
  { year: '2019', event: 'Avivan Enterprises founded with a vision to build a trusted FMCG distribution network in South India.' },
  { year: '2020', event: 'Secured authorized distributorship for Aachi Masala, starting operations in Tamil Nadu.' },
  { year: '2021', event: 'Expanded retail network to 200+ partner outlets across multiple districts.' },
  { year: '2022', event: 'Launched dedicated logistics fleet and warehouse management system for better efficiency.' },
  { year: '2023', event: 'Crossed 500+ retail partners milestone with consistent month-on-month growth.' },
  { year: '2024', event: 'Initiated expansion plans for multi-brand FMCG distribution covering 20+ categories.' },
  { year: '2025+', event: 'Building India\'s most comprehensive FMCG distribution network across multiple states.' },
];

const teamStrengths = [
  { icon: <FiShield size={20} />, title: 'Industry Expertise', desc: 'Deep knowledge of FMCG distribution dynamics and market conditions' },
  { icon: <FiUsers size={20} />, title: 'Strong Network', desc: '500+ retail partners and growing across Tamil Nadu' },
  { icon: <FiTrendingUp size={20} />, title: 'Growth Mindset', desc: 'Always innovating and expanding to serve more businesses' },
  { icon: <FaHandshake size={20} />, title: 'Partnership Focus', desc: 'We treat every partner as a long-term business ally' },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Us | Avivan Enterprises — FMCG Distribution Company India</title>
        <meta name="description" content="Learn about Avivan Enterprises - our mission, vision, values, and journey in building India's trusted FMCG distribution network." />
      </Helmet>

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="page-banner-content text-center">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 18px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
              🏢 Our Story
            </div>
            <h1>About Avivan Enterprises</h1>
            <p>Building India's most trusted FMCG distribution network, one partnership at a time</p>
            <div className="breadcrumb-c" style={{ justifyContent: 'center' }}>
              <Link to="/">Home</Link>
              <span className="sep">/</span>
              <span style={{ color: 'var(--accent)' }}>About Us</span>
            </div>
          </div>
        </div>
      </div>

      {/* Company Overview */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6" data-aos="fade-right">
              <div style={{ background: 'var(--gradient-primary)', borderRadius: 20, padding: 48, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: '3.5rem', marginBottom: 20 }}>🏭</div>
                  <h3 style={{ color: '#fff', fontWeight: 900, fontSize: '1.8rem', marginBottom: 16 }}>Avivan Enterprises</h3>
                  <p style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.75, marginBottom: 28 }}>
                    Founded with the vision of revolutionizing FMCG distribution in India, we have grown from a small distribution startup to a respected name in the supply chain industry.
                  </p>
                  <div className="row g-3">
                    {[
                      { num: '500+', lbl: 'Retail Partners' },
                      { num: '5+', lbl: 'Years Operating' },
                      { num: '10+', lbl: 'Districts Served' },
                      { num: '1', lbl: 'Premium Brand' },
                    ].map(s => (
                      <div className="col-6" key={s.lbl}>
                        <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '18px 16px', border: '1px solid rgba(255,255,255,0.12)' }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--accent)', fontFamily: 'Poppins, sans-serif' }}>{s.num}</div>
                          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '1px' }}>{s.lbl}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <span className="section-badge"><FiTarget size={12} /> Who We Are</span>
              <h2 className="section-title mt-2">A Growing <span>FMCG Distribution</span> Company</h2>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 16 }}>
                Avivan Enterprises is a dynamic and growing FMCG distribution company committed to delivering trusted products efficiently through a strong supply chain and dealer network. Headquartered in Tamil Nadu, we serve businesses of all sizes across the region.
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 24 }}>
                We currently hold the authorized distributorship for <strong style={{ color: 'var(--primary)' }}>Aachi Masala</strong>, one of India's most loved masala brands, and are rapidly building our capabilities to distribute multiple FMCG brands and categories in the coming years.
              </p>
              {[
                'Authorized distributor with direct brand partnerships',
                'Dedicated warehousing and logistics infrastructure',
                'Committed to sustainable, responsible business practices',
                'Technology-driven inventory and order management',
              ].map(c => (
                <div key={c} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                  <FiCheckCircle style={{ color: 'var(--secondary)', flexShrink: 0, marginTop: 2 }} size={17} />
                  <span style={{ fontSize: '0.92rem', color: 'var(--dark)' }}>{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge"><FiTarget size={12} /> Our Purpose</span>
            <h2 className="section-title">Mission & <span>Vision</span></h2>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            <div className="col-md-6" data-aos="fade-up" data-aos-delay="80">
              <div className="mission-card mission-vision-card h-100">
                <div className="mv-icon"><FiTarget size={28} /></div>
                <div className="mv-title">Our Mission</div>
                <div className="mv-text">
                  To build one of India's most trusted FMCG distribution networks by delivering quality products efficiently, supporting our partners' growth, and creating lasting value through innovation, reliability, and exceptional service.
                </div>
              </div>
            </div>
            <div className="col-md-6" data-aos="fade-up" data-aos-delay="160">
              <div className="vision-card mission-vision-card h-100">
                <div className="mv-icon"><FiEye size={28} /></div>
                <div className="mv-title">Our Vision</div>
                <div className="mv-text">
                  To become a leading multi-brand FMCG distribution company serving businesses across India — from small retailers to large institutional buyers — with a comprehensive portfolio spanning 20+ product categories by 2027.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge"><FiHeart size={12} /> What We Stand For</span>
            <h2 className="section-title">Our Core <span>Values</span></h2>
            <p className="section-subtitle">The principles that guide every decision, partnership, and interaction at Avivan Enterprises</p>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {values.map((v, i) => (
              <div className="col-lg-4 col-md-6" key={v.title} data-aos="fade-up" data-aos-delay={i * 70}>
                <div className="value-card">
                  <div className="value-icon" style={{ background: v.bg }}>
                    <span style={{ fontSize: '1.8rem' }}>{v.icon}</span>
                  </div>
                  <div className="value-title">{v.title}</div>
                  <p className="value-desc">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section style={{ padding: '90px 0', background: 'var(--light)' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">📅 Our Journey</span>
            <h2 className="section-title">Milestones & <span>Growth Story</span></h2>
            <div className="divider-bar" />
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              {milestones.map((m, i) => (
                <div key={m.year} className="d-flex gap-4" style={{ marginBottom: 28 }} data-aos="fade-up" data-aos-delay={i * 60}>
                  <div style={{ flexShrink: 0, textAlign: 'center' }}>
                    <div style={{ background: 'var(--gradient-primary)', color: '#fff', borderRadius: 12, padding: '10px 16px', fontWeight: 800, fontSize: '0.92rem', fontFamily: 'Poppins, sans-serif', minWidth: 72, textAlign: 'center', boxShadow: 'var(--shadow-primary)' }}>
                      {m.year}
                    </div>
                    {i < milestones.length - 1 && (
                      <div style={{ width: 2, height: 28, background: 'var(--border)', margin: '4px auto' }} />
                    )}
                  </div>
                  <div style={{ background: '#fff', borderRadius: 12, padding: '18px 24px', border: '1px solid var(--border)', flex: 1, fontSize: '0.92rem', color: 'var(--dark)', lineHeight: 1.65, boxShadow: 'var(--shadow-xs)' }}>
                    {m.event}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Strengths / Why Avivan */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge"><FiAward size={12} /> Our Edge</span>
            <h2 className="section-title">Why <span>Avivan Enterprises</span></h2>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {teamStrengths.map((s, i) => (
              <div className="col-md-6 col-lg-3" key={s.title} data-aos="fade-up" data-aos-delay={i * 80}>
                <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, textAlign: 'center', transition: 'var(--transition)', height: '100%' }} className="card-hover">
                  <div style={{ width: 58, height: 58, borderRadius: 14, background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#fff', boxShadow: 'var(--shadow-primary)' }}>
                    {s.icon}
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8 }}>{s.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0', background: 'var(--gradient-dark)' }}>
        <div className="container">
          <div className="contact-cta-content" data-aos="fade-up">
            <h2 style={{ color: '#fff', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, marginBottom: 12, textAlign: 'center' }}>
              Ready to Partner with <span style={{ color: 'var(--accent)' }}>Avivan Enterprises?</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', maxWidth: 480, margin: '0 auto 32px' }}>
              Join our growing network of dealers and distributors. Let's build a successful business together.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/become-dealer" className="btn-accent-c" style={{ fontSize: '1rem', padding: '14px 36px' }}>
                Become a Dealer <FiArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-outline-white" style={{ fontSize: '1rem', padding: '12px 34px' }}>
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
